import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "./StatusBadge";
import { STATUS_FILTERS } from "@/lib/advertising";
import { ChevronRight, BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const ALL = "__all";
const COLS = [["business_name", "Business"], ["ad_type", "Ad type"], ["assigned_to_name", "Salesperson"], ["start", "Start"], ["end", "End"], ["contract_status", "Status"]];

function Tree({ markets, sel, setSel }) {
  return (
    <nav className="cc-card p-2 h-fit max-h-[70vh] overflow-y-auto" data-testid="zone-tree">
      <button onClick={() => setSel({})} className={cn("w-full text-left rounded-lg px-2.5 py-1.5 text-sm", !sel.market && "bg-[#00C9E0]/10 text-[#0094A8] font-semibold")} data-testid="tree-all">All Markets</button>
      {markets.filter((m) => m.status !== "inactive" || m.advertisers).map((m) => (
        <div key={m.id}>
          <button onClick={() => setSel({ market: m.id })} data-testid={`tree-market-${m.id}`}
            className={cn("w-full flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm font-semibold uppercase tracking-wide text-[11px]", sel.market === m.id && !sel.zone ? "bg-[#00C9E0]/10 text-[#0094A8]" : "text-slate-500 hover:bg-slate-50")}>
            <span>{m.name}{m.status === "future" ? " · future" : ""}</span><span className="tnum">{m.advertisers}</span>
          </button>
          {m.zones.filter((z) => z.status !== "inactive" || z.advertisers).map((z) => (
            <button key={z.id} onClick={() => setSel({ market: m.id, zone: z.id })} data-testid={`tree-zone-${z.id}`}
              className={cn("w-full flex items-center justify-between rounded-lg pl-5 pr-2.5 py-1 text-sm", sel.zone === z.id ? "bg-[#00C9E0]/10 text-[#0094A8] font-semibold" : "text-slate-600 hover:bg-slate-50")}>
              <span className="flex items-center gap-1"><ChevronRight className="h-3 w-3 text-slate-300" />{z.name}</span>
              <span className="text-[11px] tnum text-slate-400 whitespace-nowrap">{z.available_positions} open</span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

export default function ZoneBrowser({ overview }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [sel, setSel] = useState({ market: params.get("market") || undefined, zone: params.get("zone") || undefined });
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: ALL, sp: ALL, ad: ALL, q: "" });
  const [sort, setSort] = useState(["business_name", 1]);
  const pMarket = params.get("market"); const pZone = params.get("zone");
  useEffect(() => { if (pMarket || pZone) setSel({ market: pMarket || undefined, zone: pZone || undefined }); }, [pMarket, pZone]);
  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams();
    if (sel.market) qs.set("market_id", sel.market);
    if (sel.zone) qs.set("zone_id", sel.zone);
    api.get(`/placements?${qs}`).then((r) => { if (live) setRows(r.data.items); });
    return () => { live = false; };
  }, [sel]);
  const shown = useMemo(() => {
    const val = (p, k) => (k === "start" ? p.contract?.start_date : k === "end" ? p.contract?.end_date : p[k]) || "";
    return rows.filter((p) => (f.status === ALL || p.contract_status === f.status) && (f.sp === ALL || p.assigned_to_name === f.sp)
      && (f.ad === ALL || p.ad_type === f.ad) && (!f.q || (p.business_name || "").toLowerCase().includes(f.q.toLowerCase())))
      .sort((a, b) => String(val(a, sort[0])).localeCompare(String(val(b, sort[0]))) * sort[1]);
  }, [rows, f, sort]);
  const uniq = (k) => [...new Set(rows.map((r) => r[k]).filter(Boolean))];
  const zone = overview?.markets.flatMap((m) => m.zones).find((z) => z.id === sel.zone);
  const pick = (k, label, opts) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger className="h-8 w-44" data-testid={`zb-filter-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>{label}</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]" data-testid="zone-browser">
      <Tree markets={overview?.markets || []} sel={sel} setSel={setSel} />
      <div className="min-w-0 space-y-3">
        {zone && (
          <div className="cc-card p-4 flex flex-wrap gap-6 text-sm" data-testid="zone-summary">
            <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Zone</p><p className="font-display font-semibold">{zone.name}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Advertisers</p><p className="font-bold tnum">{zone.advertisers}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Positions used / available</p><p className="font-bold tnum">{zone.used_positions} / <span className="text-[#4A700A]">{zone.available_positions}</span> of {zone.capacity}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Renewals upcoming</p><p className="font-bold tnum">{zone.renewals_upcoming}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Ending ≤30d</p><p className="font-bold tnum">{zone.ending_soon}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Ad breakdown</p><p className="text-slate-600">{Object.entries(zone.breakdown).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</p></div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Input className="h-8 w-52" placeholder="Search business…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="zb-search" />
          {pick("status", "All statuses", STATUS_FILTERS)}{pick("sp", "All salespeople", uniq("assigned_to_name"))}{pick("ad", "All ad types", uniq("ad_type"))}
        </div>
        <div className="cc-card overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead><tr className="border-b bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              {COLS.map(([k, l]) => <th key={k} className="px-3 py-2.5 cursor-pointer select-none" onClick={() => setSort([k, sort[0] === k ? -sort[1] : 1])}>{l}{sort[0] === k ? (sort[1] > 0 ? " ↑" : " ↓") : ""}</th>)}
              <th className="px-3 py-2.5">Zone</th><th className="px-3 py-2.5">Manual renewal stage</th><th className="px-3 py-2.5">Website</th><th className="px-3 py-2.5">Current ad</th>
            </tr></thead>
            <tbody>
              {shown.length === 0 ? <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">No placements match.</td></tr> : shown.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/businesses/${p.business_id}`)} data-testid={`zb-row-${p.id}`}>
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{p.business_name}</td>
                  <td className="px-3 py-2.5">{p.ad_type}</td>
                  <td className="px-3 py-2.5 text-slate-500">{p.assigned_to_name || "—"}</td>
                  <td className="px-3 py-2.5 tnum">{fmtDate(p.contract?.start_date)}</td>
                  <td className="px-3 py-2.5 tnum">{fmtDate(p.contract?.end_date)}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={p.contract_status} testid={`zb-status-${p.id}`} /></td>
                  <td className="px-3 py-2.5 text-slate-500">{p.zone}</td>
                  <td className="px-3 py-2.5 text-slate-500">{p.renewal_stage || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{p.website_profile_status || "—"}</td>
                  <td className="px-3 py-2.5">{p.current_ad ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#4A700A]"><BadgeCheck className="h-3.5 w-3.5" />Approved</span> : <span className="text-xs text-slate-400">None</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
